import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { Product } from "../model/product";
import { StorageService, StorageEvent } from "./storage.service";
import { Certificate, CertificateCard, PromoCode } from "../model/promo";
import { Observable } from "rxjs";
import { HttpService } from "./http.service";
import { Order } from "../model/order";

export interface CartItem {
  product: Product;
  count: number;
}

export interface CartCertificatetItem {
  certificate: CertificateCard;
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {

  products: CartItem[] = [];
  certificates: CartCertificatetItem[] = [];
  productsCount: number = 0;
  cartChange: EventEmitter<any> = new EventEmitter();

  private cartKeys = ['products', 'certificates', 'productsCount', 'promocode', 'giftCards'];

  constructor(
    private storageService: StorageService,
    private http: HttpService,
    private zone: NgZone
  ) {
    this.loadFromStorage();

    // Подписка на изменения из других вкладок
    this.storageService.watch().subscribe((event: StorageEvent) => {
      if (!event.external) return;

      if ((event.key && this.cartKeys.includes(event.key)) || event.type === 'clear') {
        this.zone.run(() => {
          this.loadFromStorage();
          this.cartChange.emit();
        });
      }
    });
  }

  private loadFromStorage() {
    this.products = [...this.storageService.getItem('products', '[]')];
    this.certificates = [...this.storageService.getItem('certificates', '[]')];
    this.productsCount = +this.storageService.getItem('productsCount', '0');
  }

  syncItems() {
    this.storageService.setItem(this.products, 'products');
    this.storageService.setItem(this.certificates, 'certificates');
    this.storageService.setItem(this.productsCount, 'productsCount');
    this.cartChange.emit();
  }

  addToCart(product: Product, count: number = 1) {
    let item = this.products.find(pr => pr.product.id == product.id);
    if (item) item.count += count;
    else this.products.push({ product, count });
    this.productsCount += count;
    this.syncItems();
  }

  addCertificatesToCart(certificate: CertificateCard, count: number = 1) {
    let item = this.certificates.find(pr => pr.certificate.id == certificate.id);
    if (item) item.count += count;
    else this.certificates.push({ certificate, count });
    this.productsCount += count;
    this.syncItems();
  }

  deleteFromCart(product: Product, count: number = 1) {
    let item = this.products.find(pr => pr.product.id == product.id);
    if (!item) return;

    if (item.count > count) item.count -= count;
    else this.products.splice(this.products.indexOf(item), 1);

    this.productsCount = Math.max(0, this.productsCount - count);
    this.syncItems();
  }

  deleteCertificateFromCart(certificate: CertificateCard, count: number = 1) {
    let item = this.certificates.find(pr => pr.certificate.id == certificate.id);
    if (!item) return;

    if (item.count > count) item.count -= count;
    else this.certificates.splice(this.certificates.indexOf(item), 1);

    this.productsCount = Math.max(0, this.productsCount - count);
    this.syncItems();
  }

  setCount(product: Product, count: number) {
    let item = this.products.find(pr => pr.product.id == product.id);
    if (item) item.count = count;

    this.productsCount = this.products.reduce((sum, p) => sum + p.count, 0);
    this.syncItems();
  }

  setCertificateCount(certificate: CertificateCard, count: number) {
    let item = this.certificates.find(pr => pr.certificate.id == certificate.id);
    if (item) item.count = count;

    this.productsCount = this.certificates.reduce((sum, c) => sum + c.count, 0) + this.productsCount;
    this.syncItems();
  }

  getCartCount(): number {
    return this.productsCount;
  }

  getProductCount(id: string): number {
    let item = this.getProducts().find(p => p.product.id === id);
    return item ? item.count : 0;
  }

  getProducts() {
    return this.products;
  }

  getCertificates() {
    return this.certificates;
  }

  setPromocodeToStorage(promocode: PromoCode) {
    this.storageService.setItem(promocode, 'promocode');
    this.cartChange.emit();
  }

  getPromocodeFromStorage(): PromoCode {
    return this.storageService.getItem('promocode', 'null');
  }

  setGiftCardsToStorage(giftCards: Certificate[]) {
    this.storageService.setItem(giftCards, 'giftCards');
    this.cartChange.emit();
  }

  getGiftCardsFromStorage(): Certificate[] {
    return this.storageService.getItem('giftCards', '[]');
  }

  deletePromocodeFromStorage() {
    this.storageService.deleteItem('promocode');
    this.cartChange.emit();
  }

  deleteGiftCardsFromStorage() {
    this.storageService.deleteItem('giftCards');
    this.cartChange.emit();
  }

  setOrdersToStorage(order: Order, orderName: string) {
    this.storageService.setItem(order, orderName);
  }

  getOrderFromStorage(orderName: string): Order {
    return this.storageService.getItem(orderName, 'null');
  }

  deleteOrderFromStorage(orderName: string) {
    this.storageService.deleteItem(orderName);
  }

  clearCart() {
    this.products = [];
    this.certificates = [];
    this.productsCount = 0;

    this.deletePromocodeFromStorage();
    this.deleteGiftCardsFromStorage();
    this.syncItems();

    this.storageService.clear();
  }

  getPromocodeInfo(code: string): Observable<any> {
    return this.http.get('api/promo_codes/', { number: code });
  }

  getGiftCardsInfo(code: string): Observable<any> {
    return this.http.get('api/certificates/', { number: code });
  }

  getCartPrices(params: any): Observable<any> {
    return this.http.post('api/get_cart/', params);
  }
}
